/* Small UI kit on the brand tokens (styles.css). Kid screens use big targets (≥ 64 px), parent screens are calmer. */
import { type ButtonHTMLAttributes, type ReactNode, useEffect, useId, useRef } from "react";
import { Link } from "@tanstack/react-router";

type Variant = "primary" | "coral" | "ghost" | "soft" | "danger";
const VARIANTS: Record<Variant, string> = {
  primary: "bg-purple text-white shadow-[0_6px_0_#5b3df5] active:translate-y-[3px] active:shadow-[0_3px_0_#5b3df5] hover:brightness-105",
  coral: "bg-coral text-white shadow-[0_6px_0_#e06a3b] active:translate-y-[3px] active:shadow-[0_3px_0_#e06a3b] hover:brightness-105",
  ghost: "bg-transparent text-ink hover:bg-white/60",
  soft: "bg-white text-ink shadow-sm ring-1 ring-black/5 hover:bg-cream",
  danger: "bg-white text-pink ring-1 ring-pink/40 hover:bg-pink/5",
};

export function Button({ variant = "primary", big, className = "", children, ...rest }: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; big?: boolean }) {
  return (
    <button
      {...rest}
      className={`font-display font-bold rounded-2xl transition ${big ? "text-2xl px-8 py-5 min-h-16" : "text-lg px-5 py-3"} ${VARIANTS[variant]} ${className}`}
    >
      {children}
    </button>
  );
}

export function Card({ children, className = "" }: { children: ReactNode; className?: string }) {
  return <div className={`bg-white/90 rounded-[var(--radius-card)] shadow-[0_8px_30px_rgba(45,42,74,0.08)] ring-1 ring-black/5 p-5 ${className}`}>{children}</div>;
}

export function Screen({ children, wide, className = "" }: { children: ReactNode; wide?: boolean; className?: string }) {
  return <main className={`mx-auto w-full ${wide ? "max-w-5xl" : "max-w-xl"} px-4 py-6 sm:py-10 ${className}`}>{children}</main>;
}

export function TopBar({ right, to = "/" }: { right?: ReactNode; to?: string }) {
  return (
    <header className="mx-auto w-full max-w-5xl px-4 pt-4 flex items-center justify-between">
      <Link to={to} className="flex items-center gap-2 no-underline">
        <img src="/img/lumi/hello.webp" alt="" className="w-10 h-10" />
        <span className="font-display font-extrabold text-2xl text-purple">Lumi Kids</span>
      </Link>
      <div className="flex items-center gap-2">{right}</div>
    </header>
  );
}

export function Title({ children, sub }: { children: ReactNode; sub?: ReactNode }) {
  return (
    <div className="mb-5">
      <h1 className="text-3xl sm:text-4xl font-extrabold text-ink leading-tight">{children}</h1>
      {sub && <p className="mt-2 text-ink-soft text-lg">{sub}</p>}
    </div>
  );
}

/** A labelled control. `group` for a set of buttons (chips, avatars) or several inputs: a <label> would name its first
    button after the whole group and press it whenever the label text is clicked. */
export function Field({ label, hint, group, children }: { label: string; hint?: string; group?: boolean; children: ReactNode }) {
  const id = useId();
  const title = <span id={group ? id : undefined} className="block font-display font-bold text-lg mb-1">{label}</span>;
  const note = hint && <span className="block text-sm text-ink-soft mt-1">{hint}</span>;
  return group
    ? <div role="group" aria-labelledby={id} className="block mb-4">{title}{children}{note}</div>
    : <label className="block mb-4">{title}{children}{note}</label>;
}

export const inputCls = "w-full rounded-xl border-2 border-purple/20 bg-white px-4 py-3 text-lg focus:border-purple outline-none";

export function Toggle({ checked, onChange, label, note }: { checked: boolean; onChange: (v: boolean) => void; label: string; note?: string }) {
  return (
    <button type="button" role="switch" aria-checked={checked} onClick={() => onChange(!checked)}
      className="w-full flex items-start gap-3 text-left py-3">
      <span className={`mt-0.5 shrink-0 w-12 h-7 rounded-full transition ${checked ? "bg-purple" : "bg-ink/20"} relative`}>
        <span className={`absolute top-0.5 w-6 h-6 rounded-full bg-white shadow transition-all ${checked ? "left-5.5" : "left-0.5"}`} />
      </span>
      <span>
        <span className="block text-base">{label}</span>
        {note && <span className="block text-xs uppercase tracking-wide text-ink-soft mt-0.5">{note}</span>}
      </span>
    </button>
  );
}

export function Chips<T extends string>({ options, value, onChange, multi, max }: {
  options: { id: T; label: string; icon?: string }[]; value: T[]; onChange: (v: T[]) => void; multi?: boolean; max?: number;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map(o => {
        const on = value.includes(o.id);
        return (
          <button key={o.id} type="button" aria-pressed={on}
            onClick={() => {
              if (!multi) return onChange([o.id]);
              if (on) return onChange(value.filter(v => v !== o.id));
              if (max && value.length >= max) return;
              onChange([...value, o.id]);
            }}
            className={`px-4 py-2.5 rounded-2xl font-bold text-base transition ring-2 ${on ? "bg-purple text-white ring-purple" : "bg-white text-ink ring-purple/15 hover:ring-purple/40"}`}>
            {o.icon && <span className="mr-1.5">{o.icon}</span>}{o.label}
          </button>
        );
      })}
    </div>
  );
}

export function Spinner({ label }: { label?: string }) {
  return (
    <div className="flex flex-col items-center gap-3 py-10 text-ink-soft">
      <img src="/img/lumi/thinking.webp" alt="" className="w-24 h-24 lumi-bob" />
      {label && <span className="font-display text-lg">{label}</span>}
    </div>
  );
}

export function ErrorText({ children }: { children?: ReactNode }) {
  if (!children) return null;
  return <p role="alert" className="text-pink font-semibold my-3">{children}</p>;
}

export function Modal({ open, onClose, children, label }: { open: boolean; onClose: () => void; children: ReactNode; label: string }) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    if (!open && d.open) d.close();
  }, [open]);
  return (
    <dialog ref={ref} aria-label={label} onClose={onClose} onCancel={onClose}
      className="m-auto rounded-3xl p-0 backdrop:bg-ink/40 max-w-md w-[calc(100%-2rem)] shadow-2xl">
      <div className="p-6">{children}</div>
    </dialog>
  );
}

export function Avatar({ id, size = 56 }: { id: string; size?: number }) {
  return <img src={`/img/avatars/${id}.webp`} alt="" width={size} height={size} className="rounded-full bg-cream ring-2 ring-white shadow" />;
}

export function money(amountMinor: number, currency: string, lang: string): string {
  try { return new Intl.NumberFormat(lang, { style: "currency", currency: currency.toUpperCase() }).format(amountMinor / 100); }
  catch { return `${(amountMinor / 100).toFixed(2)} ${currency.toUpperCase()}`; }
}
